/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV) - Initial application of VIBE_CODING_STANDARD directives.
 *                                   - Added `unit_of_measure_id` label display.
 *                                   - Integrated Spinner for loading states.
 *                                   - Added "Add New Process" dropdown for easier process addition.
 * 1.1.0:gemini-1.5-flash (CURRENT) - Integrated `useWhitelabelContext` to dynamically hide fields
 *                                   and customize field titles based on whitelabel configuration.
 *
 * -----
 * SECURITY_RISK: LOW - This component manages the details of a single component within a composite product's composition.
 *                      It handles quantities and process assignments but not sensitive data directly.
 * INTENT: Provide an editable interface for a single component and its associated processes within a composite,
 *         allowing users to define quantities, overheads, and process steps, now with whitelabeling capabilities.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure that changes to component and process data
 *                        are correctly propagated up to the parent state.
 * LLM_CONFIDENCE: 100% - Standard component for managing nested data structures with input and select fields.
 */
import React, { useState, useCallback } from 'react';
import { CompositionComponentUpdate, CompositionProcessUpdate } from '../types/costwand';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.tsx';
import { ProductLookupItem, ProcessLookupItem } from '../types/costwand';
import { Spinner } from './ui/spinner.tsx';
import { useWhitelabelContext } from '../context/WhitelabelContext.tsx'; // NEW: Import useWhitelabelContext

interface CompositionEditComponentProps {
  componentIndex: number;
  component: CompositionComponentUpdate;
  onComponentChange: (index: number, field: string, value: any) => void;
  onRemoveComponent: (index: number) => void;
  onProcessChange: (componentIndex: number, processIndex: number, field: string, value: any) => void;
  onRemoveProcess: (componentIndex: number, processIndex: number) => void;
  onAddProcess: (componentIndex: number, process_id: number, process_labels: any) => void;
  products: ProductLookupItem[];
  processes: ProcessLookupItem[];
  lookupLoading: boolean;
  entityId: string; // NEW: Add entityId prop (for the parent composite entity)
}

const CompositionEditComponent: React.FC<CompositionEditComponentProps> = ({
  componentIndex,
  component,
  onComponentChange,
  onRemoveComponent,
  onProcessChange,
  onRemoveProcess,
  onAddProcess,
  products,
  processes,
  lookupLoading,
  entityId, // NEW: Destructure entityId
}) => {
  const { whitelabelConfig } = useWhitelabelContext(); // NEW: Use whitelabel context

  const getFieldConfig = (fieldId: string) =>
    whitelabelConfig.entities?.[entityId]?.fields?.[fieldId];

  const isFieldVisible = (fieldId: string) =>
    getFieldConfig(fieldId)?.visible !== false; // Default to visible

  const getFieldTitle = (fieldId: string, defaultTitle: string) =>
    getFieldConfig(fieldId)?.title || defaultTitle;

  const [selectedAddProcessId, setSelectedAddProcessId] = useState<string>('');

  const handleInputChange = useCallback((field: string, value: any) => {
    onComponentChange(componentIndex, field, value);
  }, [componentIndex, onComponentChange]);

  const handleProcessInputChange = useCallback((processIndex: number, field: string, value: any) => {
    onProcessChange(componentIndex, processIndex, field, value);
  }, [componentIndex, onProcessChange]);

  const handleAddProcessClick = (process_id: number, process_labels: any) => {
    onAddProcess(componentIndex, process_id, process_labels);
  };

  return (
    <div className="mb-4 p-4 border rounded-md bg-white shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h4 className="font-semibold text-lg">
          {getFieldTitle('component_id', 'Component')}: {component._labels?.component_id || `ID: ${component.component_id}`}
          {component._labels?.unit_of_measure_id && ` [${component._labels.unit_of_measure_id}]`}
        </h4>
        <Button variant="destructive" size="sm" onClick={() => onRemoveComponent(componentIndex)}>
          Delete Component
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isFieldVisible('component_id') && (
          <div>
            <Label htmlFor={`component_id_${componentIndex}`}>{getFieldTitle('component_id', 'Component')}</Label>
            <Input
              type="text"
              id={`component_id_${componentIndex}`}
              value={component._labels?.component_id || String(component.component_id)}
              disabled
              className="bg-muted text-muted-foreground"
            />
          </div>
        )}
        {isFieldVisible('overhead_charge') && (
          <div>
            <Label htmlFor={`overhead_charge_${componentIndex}`}>{getFieldTitle('overhead_charge', 'Overhead Charge (%)')}</Label>
            <Input
              type="number"
              id={`overhead_charge_${componentIndex}`}
              value={String(component.overhead_charge ?? '')}
              onChange={(e) => handleInputChange('overhead_charge', parseFloat(e.target.value))}
              disabled={lookupLoading}
            />
          </div>
        )}
        {isFieldVisible('required_qty') && (
          <div>
            <Label htmlFor={`required_qty_${componentIndex}`}>{getFieldTitle('required_qty', 'Required Quantity')} (enter: {component.unit_dimension})</Label>
            <Input
              type="number"
              id={`required_qty_${componentIndex}`}
              value={String(component.required_qty ?? '')}
              onChange={(e) => handleInputChange('required_qty', parseFloat(e.target.value))}
              disabled={lookupLoading}
            />
          </div>
        )}
      </div>

      <h5 className="font-semibold mt-6 mb-2">{getFieldTitle('processes', 'Processes')}:</h5>
      {component.processes && component.processes.map((process, processIndex) => (
        <div key={processIndex} className="mb-3 p-3 border rounded-md bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {isFieldVisible('process_id') && (
              <div>
                <Label htmlFor={`process_id_${componentIndex}_${processIndex}`}>{getFieldTitle('process_id', 'Process')}</Label>
                <Select
                  onValueChange={(value) => {
                    const selectedProcess = processes.find(p => String(p.id) === value);
                    if (selectedProcess) {
                      handleProcessInputChange(processIndex, 'process_id', selectedProcess.id);
                      handleProcessInputChange(processIndex, '_labels', { process_id: selectedProcess.description });
                    }
                  }}
                  value={String(process.process_id)}
                  disabled={lookupLoading}
                >
                  <SelectTrigger id={`process_id_${componentIndex}_${processIndex}`}>
                    <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading processes...</span> : "Select a process"}>
                      {process._labels?.process_id || `ID: ${process.process_id}`}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {processes.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isFieldVisible('cost') && (
              <div>
                <Label htmlFor={`cost_${componentIndex}_${processIndex}`}>{getFieldTitle('cost', 'Cost')}</Label>
                <Input
                  type="number"
                  id={`cost_${componentIndex}_${processIndex}`}
                  value={String(process.cost ?? '')}
                  onChange={(e) => handleProcessInputChange(processIndex, 'cost', parseFloat(e.target.value))}
                  disabled={lookupLoading}
                />
              </div>
            )}
            {isFieldVisible('required_qty') && (
              <div>
                <Label htmlFor={`required_qty_${componentIndex}_${processIndex}`}>{getFieldTitle('required_qty', 'Required Quantity')}</Label>
                <Input
                  type="number"
                  id={`required_qty_${componentIndex}_${processIndex}`}
                  value={String(process.required_qty ?? '')}
                  onChange={(e) => handleProcessInputChange(processIndex, 'required_qty', parseFloat(e.target.value))}
                  disabled={lookupLoading}
                />
              </div>
            )}
          </div>
          <Button variant="destructive" size="sm" onClick={() => onRemoveProcess(componentIndex, processIndex)} className="mt-2">
            Delete Process
          </Button>
        </div>
      ))}
      <div className="mt-2">
        <Label htmlFor={`add_process_select_${componentIndex}`} className="block text-sm font-medium text-foreground mb-1">Add New Process</Label>
        <Select
          onValueChange={(value) => {
            setSelectedAddProcessId(value);
            const selectedProcess = processes.find(p => String(p.id) === value);
            if (selectedProcess) {
              handleAddProcessClick(selectedProcess.id, { process_id: selectedProcess.description });
              setSelectedAddProcessId('');
            }
          }}
          value={selectedAddProcessId}
          disabled={lookupLoading}
        >
          <SelectTrigger id={`add_process_select_${componentIndex}`}>
            <SelectValue placeholder={lookupLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Loading processes...</span> : "Select a process to add"} />
          </SelectTrigger>
          <SelectContent>
            {processes.filter((item) => {
              if(!component.processes) return true;

              let exists = component.processes.map((process) => {
                if(process.process_id == item.id) {
                  return true;
                }
              }).indexOf(true) > -1;
              
              return !exists;
            }).map((p) => (
              <SelectItem key={p.id} value={String(p.id)}>
                {p.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default CompositionEditComponent;